import { COMM_TEMPLATE_LABELS } from "./scheduleOptionalSetupService";
import { SETUP_TOGGLES, GROUP_TYPE } from "./scheduleWorkflow";
import { VISIT_TYPE } from "./scheduleVisitDraft";

export const SCHEDULE_AUDIT_COLUMNS = {
  SETUP: "ScheduleSetup",
  GROUPS: "ScheduleGroups",
  VISITS: "ScheduleVisits",
  STATUS: "ScheduleStatus",
};

const SETUP_FLAG_KEYS = [
  "studyScreening",
  "screeningRandomisationTogether",
  "visitPayment",
  "visitCost",
  "eCommunication",
  "studyWorksheet",
];

function flagLabel(key) {
  return SETUP_TOGGLES.find((t) => t.key === key)?.label || key;
}

function onOff(value) {
  return value ? "On" : "Off";
}

export function formatSetupFlagsSummary(flags = {}, { groupType } = {}) {
  const parts = SETUP_FLAG_KEYS.map(
    (key) => `${flagLabel(key)}: ${onOff(!!flags[key])}`,
  );
  if (groupType) {
    parts.unshift(`Group type: ${groupType}`);
  }
  return parts.join(" · ");
}

function parseSetupSummaryParts(text = "") {
  const map = new Map();
  String(text)
    .split("·")
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((part) => {
      const colonIndex = part.indexOf(":");
      if (colonIndex < 0) return;
      const key = part.slice(0, colonIndex).trim();
      const value = part.slice(colonIndex + 1).trim();
      if (key) map.set(key, value);
    });
  return map;
}

/** When old/new store full setup snapshots, show only differing fields in the log. */
export function narrowSetupSummaryDiff(oldText = "", newText = "") {
  const rows = buildSetupAuditDiffRows(oldText, newText);
  if (!rows.length) {
    return { oldText: String(oldText || "").trim(), newText: String(newText || "").trim() };
  }

  return {
    oldText: rows
      .filter((row) => row.oldValue)
      .map((row) => `${row.label}: ${row.oldValue}`)
      .join(" · "),
    newText: rows
      .filter((row) => row.newValue)
      .map((row) => `${row.label}: ${row.newValue}`)
      .join(" · "),
  };
}

/** One row per changed setup flag so the activity log can stack them. */
export function buildSetupAuditDiffRows(oldText = "", newText = "") {
  const oldMap = parseSetupSummaryParts(oldText);
  const newMap = parseSetupSummaryParts(newText);
  if (!oldMap.size && !newMap.size) return [];

  const keys = [...new Set([...oldMap.keys(), ...newMap.keys()])];
  return keys
    .map((label) => {
      const oldValue = oldMap.get(label) || "";
      const newValue = newMap.get(label) || "";
      if (oldValue === newValue) return null;
      return { label, oldValue, newValue };
    })
    .filter(Boolean);
}

export function diffSetupFlags(
  oldFlags = {},
  newFlags = {},
  { oldGroupType, newGroupType } = {},
) {
  const oldParts = [];
  const newParts = [];

  const oldGt = String(oldGroupType || "").trim();
  const newGt = String(newGroupType || "").trim();
  if (oldGt && newGt && oldGt !== newGt) {
    oldParts.push(`Group type: ${oldGt}`);
    newParts.push(`Group type: ${newGt}`);
  }

  SETUP_FLAG_KEYS.forEach((key) => {
    const oldVal = !!oldFlags[key];
    const newVal = !!newFlags[key];
    if (oldVal !== newVal) {
      const label = flagLabel(key);
      oldParts.push(`${label}: ${onOff(oldVal)}`);
      newParts.push(`${label}: ${onOff(newVal)}`);
    }
  });

  return {
    oldText: oldParts.join(" · "),
    newText: newParts.join(" · "),
  };
}

export function buildSetupAuditPayload({
  baselineFlags = {},
  savedFlags = {},
  groupType,
  baselineGroupType,
  isUpdate = false,
}) {
  const groupLabel = defaultGroupTypeLabel(groupType);
  const oldGroupLabel = defaultGroupTypeLabel(baselineGroupType ?? groupType);
  const savedSummary = formatSetupFlagsSummary(savedFlags, { groupType: groupLabel });
  const baselineSummary = formatSetupFlagsSummary(baselineFlags, {
    groupType: oldGroupLabel,
  });

  return {
    actionLabel: isUpdate ? "Schedule setup updated" : "Schedule setup saved",
    oldValue: isUpdate ? baselineSummary : "",
    newValue: savedSummary,
  };
}

export function formatVisitTimePoint(visit, { referenceVisits = [] } = {}) {
  if (!visit) return "";
  const name = String(visit.visitName || "").trim() || "Visit";
  const refId = String(visit.refVisitId || "").trim();
  const refName =
    String(visit.refVisitName || "").trim() ||
    (refId
      ? referenceVisits.find((row) => String(row.visitsPerStudyId) === refId)?.visitName ||
        ""
      : "");
  const refLine = refName
    ? `Reference: ${refName}`
    : refId
      ? `Reference: ${refId}`
      : "";
  if (visit.visitType === VISIT_TYPE.SCREENING || visit.isScreening) {
    return [
      `Visit: ${name}`,
      `Start: ${visit.screeningWindowStart ?? ""}`,
      `Finish: ${visit.screeningWindowFinish ?? ""}`,
      `Duration: ${visit.screeningDuration ?? ""}`,
    ].join(" · ");
  }
  if (visit.visitType === VISIT_TYPE.RESIDENTIAL) {
    return [
      `Visit: ${name}`,
      refLine,
      `Start: ${visit.startPeriod ?? ""}`,
      `Finish: ${visit.finishPeriod ?? ""}`,
      `Duration: ${visit.periodDuration ?? ""}`,
      `Tolerance: ${visit.tolerencePeriod ?? ""}`,
      `Upper: ${visit.upperTolerenceLimit ?? ""}`,
    ]
      .filter(Boolean)
      .join(" · ");
  }
  return [
    `Visit: ${name}`,
    refLine,
    `Day: ${visit.nonResidVisitOccur ?? ""}`,
    `Tolerance: ${visit.nonResidTolerencePeriod ?? ""}`,
    `Upper: ${visit.nonResidUpperTolerenceLimit ?? ""}`,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function formatPlannerSetup({ bookingPerSlot, slotDuration, visitName } = {}) {
  const parts = [];
  if (visitName) parts.push(`Visit: ${String(visitName).trim()}`);
  parts.push(`Booking per slot: ${bookingPerSlot ?? ""}`);
  parts.push(`Slot duration: ${slotDuration ?? ""}`);
  return parts.filter((part) => !part.endsWith(": ")).join(" · ");
}

export function formatPaymentAudit({ payment, currency = "£" }) {
  return `Payment: ${currency}${payment ?? ""}`;
}

export function formatCostAudit({ visitCost, randomCost, currency = "£", isScreening = false }) {
  const parts = [`Cost: ${currency}${visitCost ?? ""}`];
  if (!isScreening && randomCost != null && String(randomCost).trim() !== "") {
    parts.push(`Randomisation cost: ${currency}${randomCost}`);
  }
  return parts.join(" · ");
}

function plainTextFromHtml(html = "") {
  return String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function truncateAuditText(text = "", max = 500) {
  const value = String(text || "").trim();
  if (!value) return "";
  if (value.length <= max) return value;
  return `${value.slice(0, max)}…`;
}

function ecommTemplateSnapshot(template) {
  if (!template) return null;
  return {
    email: !!template.IsEmail,
    sms: !!template.IsSMS,
    subject: String(template.EmailSubject || "").trim(),
    body: truncateAuditText(plainTextFromHtml(template.EmailText), 300),
    smsText: truncateAuditText(template.SMSText, 160),
  };
}

export function ecommTemplateHasContent(template) {
  const snap = ecommTemplateSnapshot(template);
  if (!snap) return false;
  return !!(snap.subject || snap.body || snap.smsText || snap.email || snap.sms);
}

export function formatEcommTemplateAudit(template) {
  if (!template) return "";
  const snap = ecommTemplateSnapshot(template);
  const label = COMM_TEMPLATE_LABELS[template.TemplateType] || template.TemplateType;
  const parts = [`Template: ${label}`];
  parts.push(`Email: ${snap.email ? "On" : "Off"}`);
  parts.push(`SMS: ${snap.sms ? "On" : "Off"}`);
  if (snap.subject) parts.push(`Email subject: ${snap.subject}`);
  if (snap.body) parts.push(`Email body: ${snap.body}`);
  if (snap.smsText) parts.push(`SMS text: ${snap.smsText}`);
  return parts.join(" · ");
}

export function buildEcommTemplateAuditPayload({
  beforeTemplate,
  afterTemplate,
  templateType,
  visitName = "",
}) {
  const before = ecommTemplateSnapshot(beforeTemplate);
  const after = ecommTemplateSnapshot(afterTemplate);
  const label = COMM_TEMPLATE_LABELS[templateType] || templateType;
  const prefix = [
    visitName ? `Visit: ${visitName}` : "",
    `Template: ${label}`,
  ]
    .filter(Boolean)
    .join(" · ");

  const oldParts = [];
  const newParts = [];
  const compare = [
    ["Email", "email"],
    ["SMS", "sms"],
    ["Email subject", "subject"],
    ["Email body", "body"],
    ["SMS text", "smsText"],
  ];

  compare.forEach(([labelText, key]) => {
    const oldVal =
      key === "email" || key === "sms"
        ? before?.[key]
          ? "On"
          : "Off"
        : before?.[key] || "";
    const newVal =
      key === "email" || key === "sms"
        ? after?.[key]
          ? "On"
          : "Off"
        : after?.[key] || "";
    if (String(oldVal) === String(newVal)) return;
    oldParts.push(`${labelText}: ${oldVal || "—"}`);
    newParts.push(`${labelText}: ${newVal || "—"}`);
  });

  const hadContent = ecommTemplateHasContent(beforeTemplate);
  const hasChanges = oldParts.length > 0;
  const isUpdate = hadContent && hasChanges;

  return {
    actionLabel: ecommTemplateActionLabel(templateType, isUpdate),
    oldValue: hasChanges
      ? [prefix, oldParts.join(" · ")].filter(Boolean).join(" · ")
      : "",
    newValue: hasChanges
      ? [prefix, newParts.join(" · ")].filter(Boolean).join(" · ")
      : [prefix, formatEcommTemplateAudit(afterTemplate)].filter(Boolean).join(" · "),
    comments: ecommTemplateAuditComments(templateType, visitName),
  };
}

const APPOINTMENT_TEMPLATE_LABELS = {
  Booking: "Booking",
  Reschedule: "Reschedule",
  Cancellation: "Cancel",
  Reminder: "Reminder",
  LocationChange: "Location Change",
};

export function ecommTemplateActionLabel(templateType, isUpdate) {
  const appointment =
    APPOINTMENT_TEMPLATE_LABELS[templateType] ||
    COMM_TEMPLATE_LABELS[templateType] ||
    templateType;
  return isUpdate
    ? `Template updated for appointment ${appointment}`
    : `Template saved for appointment ${appointment}`;
}

export function ecommTemplateAuditComments(templateType, visitName = "") {
  const label = COMM_TEMPLATE_LABELS[templateType] || templateType;
  const parts = [];
  if (visitName) parts.push(`Visit: ${visitName}`);
  parts.push(`Template: ${label}`);
  return parts.join(" · ");
}

export function formatVisitAdded(visit, { groupName } = {}) {
  const parts = [];
  if (groupName) parts.push(`Group: ${groupName}`);
  parts.push(`Visit: ${String(visit?.visitName || "").trim() || "Visit"}`);
  if (visit?.visitType) parts.push(`Type: ${visit.visitType}`);
  return parts.join(" · ");
}

export function formatVisitDeleted(visit, { groupName } = {}) {
  const parts = [];
  if (groupName) parts.push(`Group: ${groupName}`);
  parts.push(`Visit: ${String(visit?.visitName || "").trim() || "Visit"}`);
  return parts.join(" · ");
}

export function formatGroupChange({ oldName, newName, action }) {
  if (action === "added") return `Group: ${newName}`;
  if (action === "deleted") return `Group: ${oldName}`;
  return `Old: ${oldName} · New: ${newName}`;
}

export function formatDocumentAudit({ name, url, version, action }) {
  const parts = [`Document: ${name || "Document"}`];
  if (version) parts.push(`Version: ${version}`);
  if (action === "uploaded" && url) parts.push(`Link: ${url}`);
  if (action === "deleted") parts.push("Status: Removed");
  return parts.join(" · ");
}

export function formatPublishAudit({ comments, isLiveUpdate }) {
  const parts = [];
  if (comments) parts.push(`Reason: ${comments}`);
  const now = new Date();
  parts.push(
    `Published at: ${now.toLocaleString("en-GB", { hour12: true })}`,
  );
  if (isLiveUpdate) parts.push("Mode: Live schedule update");
  return parts.join(" · ");
}

export function visitAuditSectionLabel(section) {
  switch (section) {
    case "visit_name":
      return "rename";
    case "screening":
      return "screening";
    case "time_point":
    default:
      return "time_point";
  }
}

export function defaultGroupTypeLabel(groupType) {
  return groupType === GROUP_TYPE.MULTIPLE ? GROUP_TYPE.MULTIPLE : GROUP_TYPE.SINGLE;
}
